import React, { useState } from "react";
import "./TemplateGallery.css";

const TEMPLATES = [
  {
    id: "weather",
    name: "Weather Dashboard",
    description: "Current conditions, hourly forecast, and 5-day outlook for any location.",
    icon: "🌤",
    category: "Data",
    prompt: "Create a weather dashboard for my lobby. Show current temperature, conditions, humidity, wind, and a 5-day forecast. Use Open-Meteo (free). Location: New York City.",
  },
  {
    id: "clock",
    name: "World Clock",
    description: "Display time and date across multiple time zones. Perfect for offices with global teams.",
    icon: "🕐",
    category: "Utility",
    prompt: "Create a world clock dashboard showing the current time and date for New York, London, Tokyo, and Sydney. Large digital display, dark theme.",
  },
  {
    id: "quotes",
    name: "Daily Inspiration",
    description: "A beautiful rotating display of motivational quotes that changes throughout the day.",
    icon: "💬",
    category: "Content",
    prompt: "Create a daily inspiration dashboard that shows a motivational quote. Use a free quotes API. Elegant typography with subtle animations. Change quote every hour.",
  },
  {
    id: "news",
    name: "News Headlines",
    description: "Live news ticker with top headlines from multiple sources.",
    icon: "📰",
    category: "Data",
    prompt: "Create a news headlines dashboard that shows the latest top news stories. Use a free news API. Show headline, source, and time. Auto-rotate through stories.",
  },
  {
    id: "social",
    name: "Social Media Wall",
    description: "Display a curated feed of social media posts and engagement metrics.",
    icon: "📱",
    category: "Content",
    prompt: "Create a social media metrics dashboard. Show follower counts, recent engagement stats, and trending hashtags. Use placeholder data that can be connected to real APIs later.",
  },
  {
    id: "kpi",
    name: "KPI Dashboard",
    description: "Key performance indicators with charts, numbers, and trend arrows.",
    icon: "📊",
    category: "Business",
    prompt: "Create a KPI dashboard with 6 metric cards showing: Revenue, Orders, Customers, Conversion Rate, Avg Order Value, and Customer Satisfaction. Use sample data with trend indicators (up/down arrows). Professional dark theme.",
  },
  {
    id: "menu",
    name: "Digital Menu Board",
    description: "Restaurant-style menu display with categories, items, prices, and images.",
    icon: "🍽",
    category: "Retail",
    prompt: "Create a digital menu board for a café. Categories: Coffee, Tea, Pastries, Sandwiches. Show item names, descriptions, and prices. Warm color scheme, appetizing layout.",
  },
  {
    id: "events",
    name: "Event Schedule",
    description: "Upcoming events with times, locations, and descriptions. Great for conference rooms.",
    icon: "📅",
    category: "Utility",
    prompt: "Create an event schedule board showing today's meetings and upcoming events. Show time, title, location, and organizer. Include a live clock at the top. Conference room style.",
  },
  {
    id: "wayfinding",
    name: "Directory & Wayfinding",
    description: "Building directory with floor maps and tenant listings.",
    icon: "🗺",
    category: "Utility",
    prompt: "Create a building directory display. Show a list of tenants/offices with floor numbers and suite numbers. Include a simple floor indicator. Clean, modern lobby style.",
  },
  {
    id: "countdown",
    name: "Event Countdown",
    description: "Countdown timer to a specific event with days, hours, minutes, seconds.",
    icon: "⏳",
    category: "Content",
    prompt: "Create a countdown timer dashboard. Show a large countdown to a configurable date with days, hours, minutes, and seconds. Add a title and subtitle. Dramatic dark theme with glowing numbers.",
  },
  {
    id: "leaderboard",
    name: "Leaderboard",
    description: "Rankings and scores for sales teams, competitions, or gamification.",
    icon: "🏆",
    category: "Business",
    prompt: "Create a sales leaderboard showing top 10 performers with their names, photos (use initials as avatars), sales numbers, and ranking. Include a podium for top 3. Competitive, energetic design.",
  },
  {
    id: "transport",
    name: "Transit Departures",
    description: "Real-time transit departure board for nearby bus, train, or subway stops.",
    icon: "🚇",
    category: "Data",
    prompt: "Create a transit departure board showing upcoming departures. Display line/route, destination, and minutes until arrival. Style it like a classic station departure board with dark background and bright text.",
  },
];

const CATEGORIES = ["All", ...new Set(TEMPLATES.map((t) => t.category))];

export default function TemplateGallery({ onSelectTemplate, onCustom }) {
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");

  const filtered = TEMPLATES.filter((t) => {
    const matchCat = category === "All" || t.category === category;
    const matchSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.description.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div className="template-gallery">
      <div className="tg-header">
        <h2>What would you like to display?</h2>
        <p>Choose a template to get started, or describe your own.</p>
      </div>

      <div className="tg-controls">
        <input
          className="tg-search"
          type="text"
          placeholder="Search templates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="tg-categories">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              className={`tg-cat-btn ${category === cat ? "active" : ""}`}
              onClick={() => setCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      <div className="tg-grid">
        {filtered.map((t) => (
          <button key={t.id} className="tg-card" onClick={() => onSelectTemplate(t)}>
            <span className="tg-card-icon">{t.icon}</span>
            <div className="tg-card-text">
              <span className="tg-card-name">{t.name}</span>
              <span className="tg-card-desc">{t.description}</span>
            </div>
          </button>
        ))}
      </div>

      <div className="tg-custom">
        <p>Don't see what you need?</p>
        <button className="tg-custom-btn" onClick={onCustom}>
          Describe your own integration
        </button>
      </div>
    </div>
  );
}
